import { navigateTo, useRuntimeConfig } from 'nuxt/app'
import { defineStore } from 'pinia'

/**
 * @description Pinia store for user data and sign in/out functions.
 * @function useUserStore
 * @returns {Object} The user store with the following properties:
 * @property {Object} state - The state of the user store.
 * @property {string} state.userName - The user's name.
 * @property {string} state.password - The user's password.
 * @property {string} state.fullName - The user's full name.
 * @property {string} state.role - The user's role.
 * @property {string} state.token - The user's token.
 * @property {function} actions.setToken - Set the user's token.
 * @property {function} actions.setUserName - Set the user's name.
 * @property {function} actions.setPassword - Set the user's password.
 * @property {function} actions.setRole - Set the user's role.
 * @property {function} actions.setFullName - Set the user's full name.
 * @property {function} actions.getUserData - Get the user's data from the API.
 * @property {function} actions.signIn - Sign in the user.
 * @property {function} actions.signOut - Sign out the user.
 */
export const useUserStore = defineStore('user', {

  /**
   * Store id
   */
  id: 'user',
  state: () => {
    /**
     * User data
     */
    return {
      userName: '',
      password: '',
      fullName: '',
      role: '',
      token: window.localStorage.getItem('token') || '',
      orgUnitCode: null,
    }
  },

  /**
   * Store functions
   */
  actions: {
    setToken(data) {this.token = data},
    setUserName(data) {this.userName = data},
    setPassword(data) {this.password = data},
    setRole(data) {this.role = data},
    setFullName(data) {this.fullName = data},
    setOrgUnitCode(data) {this.orgUnitCode = data},

    /**
     * Get user data from api after receiving token by login
     */
    async getUserData() {
      await fetch(useRuntimeConfig().public.apiBase + "/me", {
          method: 'GET',
          headers: {
      			"Content-Type": "application/json",
      			"Authorization": "Bearer " + this.token,
      		},

        }).then(async res => {
          const resObject = await res.json()
          this.role = resObject.role
          this.fullName = resObject.fullName
          this.orgUnitCode = resObject.orgUnitCode
        })
    },


    /**
     * Sign in user by name
     */
    async signIn(newName, rememberMe = false) {
        const config = useRuntimeConfig()
        
        await fetch(config.public.apiBase + "/login/test/" + newName + "/token",{
          headers: {"Content-Type": "application/json"},
          method: 'GET',
        }).then(async res => {
          const tokenObject = await res.json()
          this.setToken(tokenObject.token)
          this.setUserName(newName)

          if (rememberMe) {
            window.localStorage.setItem('token', tokenObject.token)
          }

          await this.getUserData()
          return navigateTo('/')
        })
    },

    /**
     * Sign out user
     * Remove user data and navigate to login page
     */
    signOut() {
      this.setToken('')
      this.setUserName('')
      this.setPassword('')
      window.localStorage.removeItem('token')
      return navigateTo("/login")
    },
  },
})

